/**
 * LLM Providers administration page.
 * Configure AI providers for enrichment features.
 */

import { useState } from "react";
import {
  useLLMProviders,
  useLLMProviderPresets,
  useCreateLLMProviderFromPreset,
  useUpdateLLMProvider,
  useDeleteLLMProvider,
  useTestLLMProvider,
} from "@/api/hooks";
import { Button } from "@/components/ui/button";
import type { LLMProvider, LLMProviderPreset } from "@/api/types";
import {
  Plus,
  Loader2,
  AlertCircle,
  Pencil,
  Trash2,
  Cpu,
  CheckCircle2,
  XCircle,
  PlayCircle,
  Star,
  Zap,
  Server,
} from "lucide-react";

// Provider type icons
const providerIcons: Record<string, typeof Cpu> = {
  openai_compatible: Zap,
  anthropic: Cpu,
  google: Server,
};

// Provider preset display info
const presetInfo: Record<string, { color: string; description: string }> = {
  openai: { color: "bg-emerald-500", description: "GPT-4o, GPT-4 Turbo" },
  deepseek: { color: "bg-blue-500", description: "DeepSeek Chat, Coder, Reasoner" },
  mistral: { color: "bg-orange-500", description: "Mistral Large, Codestral" },
  groq: { color: "bg-purple-500", description: "Llama 3.1 ultra-rapide" },
  together: { color: "bg-pink-500", description: "Together AI" },
  ollama: { color: "bg-gray-500", description: "Serveur local Ollama" },
  llamacpp: { color: "bg-gray-600", description: "Serveur local llama.cpp" },
  anthropic: { color: "bg-amber-600", description: "Claude Sonnet, Opus, Haiku" },
};

export function LLMProvidersPage() {
  const { data: providers, isLoading, error } = useLLMProviders();
  const { data: presetsData } = useLLMProviderPresets();
  const createFromPreset = useCreateLLMProviderFromPreset();
  const updateProvider = useUpdateLLMProvider();
  const deleteProvider = useDeleteLLMProvider();
  const testProvider = useTestLLMProvider();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState<LLMProvider | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<
    Record<string, { success: boolean; message: string }>
  >({});

  const handleTest = async (provider: LLMProvider) => {
    setTestingId(provider.id);
    try {
      const result = await testProvider.mutateAsync(provider.id);
      setTestResults((prev) => ({
        ...prev,
        [provider.id]: {
          success: result.success,
          message: result.success
            ? `OK (${String(result.processingTimeMs ?? 0)}ms)`
            : (result.error ?? "Erreur inconnue"),
        },
      }));
    } catch {
      setTestResults((prev) => ({
        ...prev,
        [provider.id]: { success: false, message: "Erreur de connexion" },
      }));
    }
    setTestingId(null);
  };

  const handleToggleEnabled = async (provider: LLMProvider) => {
    await updateProvider.mutateAsync({
      id: provider.id,
      isEnabled: !provider.isEnabled,
    });
  };

  const handleSetDefault = async (provider: LLMProvider) => {
    await updateProvider.mutateAsync({
      id: provider.id,
      isDefault: true,
    });
  };

  const handleDelete = async (provider: LLMProvider) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le provider "${provider.name}" ?`)) {
      await deleteProvider.mutateAsync(provider.id);
    }
  };

  const handleCreateFromPreset = async (presetKey: string, apiKey: string) => {
    const input: { preset: string; apiKey?: string; isEnabled: boolean } = {
      preset: presetKey,
      isEnabled: true,
    };
    if (apiKey) {
      input.apiKey = apiKey;
    }
    await createFromPreset.mutateAsync(input);
    setShowAddModal(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Fournisseurs IA</h1>
          <p className="text-muted-foreground">
            Configurez les providers LLM pour l'enrichissement automatique
          </p>
        </div>
        <Button
          onClick={() => {
            setShowAddModal(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Ajouter un provider
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : error ? (
        <div className="text-destructive flex flex-col items-center justify-center py-12">
          <AlertCircle className="mb-4 h-12 w-12" />
          <p>Erreur lors du chargement des providers</p>
        </div>
      ) : providers && providers.length === 0 ? (
        <EmptyState
          onAdd={() => {
            setShowAddModal(true);
          }}
        />
      ) : (
        <div className="grid gap-4">
          {providers?.map((provider) => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              onTest={() => handleTest(provider)}
              onToggleEnabled={() => handleToggleEnabled(provider)}
              onSetDefault={() => handleSetDefault(provider)}
              onEdit={() => {
                setEditingProvider(provider);
              }}
              onDelete={() => handleDelete(provider)}
              isTesting={testingId === provider.id}
              testResult={testResults[provider.id]}
            />
          ))}
        </div>
      )}

      {/* Add Provider Modal */}
      {showAddModal && presetsData && (
        <AddProviderModal
          presets={presetsData.presets}
          onClose={() => {
            setShowAddModal(false);
          }}
          onAdd={handleCreateFromPreset}
          isLoading={createFromPreset.isPending}
        />
      )}

      {/* Edit Provider Modal */}
      {editingProvider && (
        <EditProviderModal
          provider={editingProvider}
          onClose={() => {
            setEditingProvider(null);
          }}
          onSave={async (data) => {
            await updateProvider.mutateAsync({ id: editingProvider.id, ...data });
            setEditingProvider(null);
          }}
          isLoading={updateProvider.isPending}
        />
      )}
    </div>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed py-16">
      <Cpu className="text-muted-foreground mb-4 h-16 w-16" />
      <h2 className="mb-2 text-xl font-semibold">Aucun provider configuré</h2>
      <p className="text-muted-foreground mb-6 max-w-md text-center">
        Configurez un fournisseur d'IA pour activer l'enrichissement automatique des notices
        bibliographiques (résumés, mots-clés, classification).
      </p>
      <Button onClick={onAdd}>
        <Plus className="mr-2 h-4 w-4" />
        Ajouter un provider
      </Button>
    </div>
  );
}

function ProviderCard({
  provider,
  onTest,
  onToggleEnabled,
  onSetDefault,
  onEdit,
  onDelete,
  isTesting,
  testResult,
}: {
  provider: LLMProvider;
  onTest: () => void;
  onToggleEnabled: () => void;
  onSetDefault: () => void;
  onEdit: () => void;
  onDelete: () => void;
  isTesting: boolean;
  testResult: { success: boolean; message: string } | undefined;
}) {
  const Icon = providerIcons[provider.type] || Cpu;

  return (
    <div
      className={`rounded-lg border p-4 ${
        provider.isEnabled ? "bg-card" : "bg-muted/50 opacity-75"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-lg ${
              provider.isEnabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
            }`}
          >
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">{provider.name}</h3>
              {provider.isDefault && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900 dark:text-amber-300">
                  <Star className="h-3 w-3" />
                  Par défaut
                </span>
              )}
              {!provider.isEnabled && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                  Désactivé
                </span>
              )}
            </div>
            <p className="text-muted-foreground text-sm">
              {provider.typeLabel} • {provider.defaultModel}
            </p>
            <p className="text-muted-foreground mt-1 text-xs">{provider.baseUrl}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Test result */}
          {testResult && (
            <span
              className={`flex items-center gap-1 text-xs ${
                testResult.success ? "text-green-600" : "text-red-600"
              }`}
            >
              {testResult.success ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              {testResult.message}
            </span>
          )}

          {/* Actions */}
          <Button
            variant="outline"
            size="sm"
            onClick={onTest}
            disabled={isTesting || !provider.isEnabled}
          >
            {isTesting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <PlayCircle className="h-4 w-4" />
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete}>
            <Trash2 className="text-destructive h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Actions row */}
      <div className="mt-4 flex items-center gap-4 border-t pt-4">
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={provider.isEnabled}
            onChange={onToggleEnabled}
            className="h-4 w-4 rounded border-gray-300"
          />
          <span className="text-sm">Activé</span>
        </label>
        {!provider.isDefault && provider.isEnabled && (
          <Button variant="link" size="sm" className="text-xs" onClick={onSetDefault}>
            Définir par défaut
          </Button>
        )}
        <span className="text-muted-foreground ml-auto text-xs">Priorité: {provider.priority}</span>
      </div>
    </div>
  );
}

function AddProviderModal({
  presets,
  onClose,
  onAdd,
  isLoading,
}: {
  presets: Record<string, LLMProviderPreset>;
  onClose: () => void;
  onAdd: (preset: string, apiKey: string) => void;
  isLoading: boolean;
}) {
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedPreset) {
      onAdd(selectedPreset, apiKey);
    }
  };

  return (
    <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <div className="bg-card w-full max-w-2xl rounded-lg border p-6 shadow-lg">
        <h2 className="mb-4 text-xl font-semibold">Ajouter un fournisseur IA</h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="text-muted-foreground mb-2 block text-sm">
              Sélectionnez un provider
            </label>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(presets).map(([key, preset]) => {
                const info = presetInfo[key] || { color: "bg-gray-500", description: "" };
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => {
                      setSelectedPreset(key);
                    }}
                    className={`flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-colors ${
                      selectedPreset === key
                        ? "border-primary bg-primary/5"
                        : "bg-muted/50 hover:bg-muted border-transparent"
                    }`}
                  >
                    <div className={`mt-0.5 h-3 w-3 rounded-full ${info.color}`} />
                    <div>
                      <p className="font-medium">{preset.name}</p>
                      <p className="text-muted-foreground text-xs">{info.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {selectedPreset && !["ollama", "llamacpp"].includes(selectedPreset) && (
            <div className="mb-4">
              <label className="text-muted-foreground mb-2 block text-sm">
                Clé API {selectedPreset === "anthropic" ? "(Anthropic)" : ""}
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                }}
                placeholder={
                  selectedPreset === "deepseek"
                    ? "sk-..."
                    : selectedPreset === "anthropic"
                      ? "sk-ant-..."
                      : "sk-..."
                }
                className="border-input bg-background w-full rounded-md border px-3 py-2"
              />
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={!selectedPreset || isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Ajouter
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditProviderModal({
  provider,
  onClose,
  onSave,
  isLoading,
}: {
  provider: LLMProvider;
  onClose: () => void;
  onSave: (data: { name?: string; apiKey?: string; timeout?: number; priority?: number }) => void;
  isLoading: boolean;
}) {
  const [name, setName] = useState(provider.name);
  const [apiKey, setApiKey] = useState("");
  const [timeout, setTimeout] = useState(provider.timeout);
  const [priority, setPriority] = useState(provider.priority);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: { name?: string; apiKey?: string; timeout?: number; priority?: number } = {
      timeout,
      priority,
    };
    if (name !== provider.name) {
      data.name = name;
    }
    if (apiKey) {
      data.apiKey = apiKey;
    }
    onSave(data);
  };

  return (
    <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
      <div className="bg-card w-full max-w-md rounded-lg border p-6 shadow-lg">
        <h2 className="mb-4 text-xl font-semibold">Modifier {provider.name}</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-muted-foreground mb-1 block text-sm">Nom</label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              className="border-input bg-background w-full rounded-md border px-3 py-2"
            />
          </div>

          <div>
            <label className="text-muted-foreground mb-1 block text-sm">
              Nouvelle clé API (laisser vide pour conserver)
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
              }}
              placeholder="••••••••"
              className="border-input bg-background w-full rounded-md border px-3 py-2"
            />
            {provider.hasApiKey && (
              <p className="text-muted-foreground mt-1 text-xs">
                Clé actuelle: {provider.apiKeyMasked}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-muted-foreground mb-1 block text-sm">Timeout (s)</label>
              <input
                type="number"
                value={timeout}
                onChange={(e) => {
                  setTimeout(parseInt(e.target.value));
                }}
                min={10}
                max={300}
                className="border-input bg-background w-full rounded-md border px-3 py-2"
              />
            </div>
            <div>
              <label className="text-muted-foreground mb-1 block text-sm">Priorité</label>
              <input
                type="number"
                value={priority}
                onChange={(e) => {
                  setPriority(parseInt(e.target.value));
                }}
                min={1}
                max={1000}
                className="border-input bg-background w-full rounded-md border px-3 py-2"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enregistrer
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
